import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { authApi } from '../../services/api';
import { setCredentials, selectIsAuth, selectPortalBase } from '../../store/slices/authSlice';

const schema = yup.object({ email: yup.string().email().required(), password: yup.string().required() });

/* ─────────────────────────────────────────────────────────────────────────
   RECLINER PARTS – each drawn as a detailed SVG component
───────────────────────────────────────────────────────────────────────── */

/** Full power recliner chair – animates between upright and fully reclined */
function ReclinedChair({ style }) {
  // Shared animation timing: 0→upright, 0.32→reclined, 0.62→hold, 0.87→upright, 1→hold
  const reclineTimes = "0; 0.32; 0.62; 0.87; 1";
  const reclinerSplines = "0.45 0 0.55 1; 0.5 0.5 0.5 0.5; 0.45 0 0.55 1; 0.5 0.5 0.5 0.5";
  const dur = "7s";

  return (
    <svg viewBox="0 0 340 240" fill="none" style={style} className="absolute furniture-float-1">
      {/* ── Base platform ── */}
      <rect x="30" y="200" width="280" height="18" rx="6" fill="#3d2b1a"/>
      <rect x="50" y="212" width="12" height="20" rx="3" fill="#2a1e10"/>
      <rect x="95" y="212" width="12" height="20" rx="3" fill="#2a1e10"/>
      <rect x="220" y="212" width="12" height="20" rx="3" fill="#2a1e10"/>
      <rect x="265" y="212" width="12" height="20" rx="3" fill="#2a1e10"/>

      {/* ── Scissor mechanism / recliner frame ── */}
      <line x1="70" y1="200" x2="140" y2="145" stroke="#b0842a" strokeWidth="5" strokeLinecap="round"/>
      <line x1="140" y1="200" x2="70" y2="145" stroke="#b0842a" strokeWidth="5" strokeLinecap="round"/>
      <line x1="180" y1="200" x2="250" y2="145" stroke="#b0842a" strokeWidth="5" strokeLinecap="round"/>
      <line x1="250" y1="200" x2="180" y2="145" stroke="#b0842a" strokeWidth="5" strokeLinecap="round"/>
      <circle cx="105" cy="172" r="5" fill="#d4a030"/>
      <circle cx="215" cy="172" r="5" fill="#d4a030"/>

      {/* ── Seat ── */}
      <rect x="55" y="130" width="230" height="60" rx="14" fill="#8B5E3C"/>
      <rect x="65" y="136" width="210" height="48" rx="10" fill="#A0714F"/>
      <circle cx="115" cy="160" r="4" fill="#7a5535" opacity="0.7"/>
      <circle cx="170" cy="160" r="4" fill="#7a5535" opacity="0.7"/>
      <circle cx="225" cy="160" r="4" fill="#7a5535" opacity="0.7"/>

      {/* ── Back – animates from upright (0°) to fully reclined (−42°) ── */}
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values={`0 280 140; -42 280 140; -42 280 140; 0 280 140; 0 280 140`}
          keyTimes={reclineTimes} dur={dur} repeatCount="indefinite"
          calcMode="spline" keySplines={reclinerSplines}/>
        <rect x="255" y="40" width="60" height="130" rx="14" fill="#8B5E3C"/>
        <rect x="263" y="48" width="44" height="114" rx="10" fill="#A0714F"/>
        <circle cx="285" cy="90" r="4" fill="#7a5535" opacity="0.7"/>
        <circle cx="285" cy="118" r="4" fill="#7a5535" opacity="0.7"/>
      </g>

      {/* ── Headrest – same rotation as back ── */}
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values={`0 280 140; -42 280 140; -42 280 140; 0 280 140; 0 280 140`}
          keyTimes={reclineTimes} dur={dur} repeatCount="indefinite"
          calcMode="spline" keySplines={reclinerSplines}/>
        <rect x="258" y="18" width="54" height="30" rx="12" fill="#9B6E4C"/>
      </g>

      {/* ── Footrest – animates from tucked (−22°) to fully extended (+18°) ── */}
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values={`-22 55 190; 18 55 190; 18 55 190; -22 55 190; -22 55 190`}
          keyTimes={reclineTimes} dur={dur} repeatCount="indefinite"
          calcMode="spline" keySplines={reclinerSplines}/>
        <rect x="-20" y="155" width="90" height="42" rx="12" fill="#8B5E3C"/>
        <rect x="-12" y="162" width="74" height="28" rx="8" fill="#A0714F"/>
        <circle cx="20" cy="176" r="3" fill="#7a5535" opacity="0.7"/>
        <circle cx="48" cy="176" r="3" fill="#7a5535" opacity="0.7"/>
      </g>

      {/* ── Left armrest ── */}
      <rect x="38" y="118" width="30" height="64" rx="12" fill="#7a5535"/>
      <rect x="42" y="122" width="22" height="20" rx="8" fill="#9B6E4C"/>

      {/* ── Right armrest ── */}
      <rect x="272" y="118" width="30" height="64" rx="12" fill="#7a5535"/>
      <rect x="276" y="122" width="22" height="20" rx="8" fill="#9B6E4C"/>

      {/* ── Linear actuator – rod extends as chair reclines ── */}
      <rect x="100" y="152" height="10" rx="4" fill="#c8a040" opacity="0.6">
        <animate attributeName="width"
          values="50; 140; 140; 50; 50"
          keyTimes={reclineTimes} dur={dur} repeatCount="indefinite"
          calcMode="spline" keySplines={reclinerSplines}/>
      </rect>
      {/* Fixed left connector */}
      <rect x="110" y="155" width="8" height="5" rx="2" fill="#e0b850" opacity="0.8"/>
      {/* Right connector slides with rod */}
      <rect y="155" width="8" height="5" rx="2" fill="#e0b850" opacity="0.8">
        <animate attributeName="x"
          values="142; 222; 222; 142; 142"
          keyTimes={reclineTimes} dur={dur} repeatCount="indefinite"
          calcMode="spline" keySplines={reclinerSplines}/>
      </rect>

      {/* ── Label dot ── */}
      <circle cx="170" cy="110" r="3" fill="#f59e0b" opacity="0.5"/>

      {/* ── RF receiver – antenna on right armrest, lights up on signal arrival ── */}
      {/* Antenna mast */}
      <line x1="287" y1="118" x2="287" y2="102" stroke="#4ade80" strokeWidth="1.5" opacity="0.65"/>
      {/* Antenna tip arms */}
      <line x1="282" y1="108" x2="287" y2="102" stroke="#4ade80" strokeWidth="1.5" opacity="0.65"/>
      <line x1="292" y1="108" x2="287" y2="102" stroke="#4ade80" strokeWidth="1.5" opacity="0.65"/>
      {/* Reception LED at base */}
      <circle cx="287" cy="122" r="3" fill="#4ade80">
        <animate attributeName="opacity" values="0.2;1;0.2" dur="1s" begin="3.0s" repeatCount="indefinite"/>
      </circle>
      {/* Expanding ring burst – synced to packet arrival every 1 s */}
      <circle cx="287" cy="122" r="3" fill="none" stroke="#4ade80" strokeWidth="1.2">
        <animate attributeName="r" values="3;20;3" dur="1s" begin="3.0s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.85;0;0.85" dur="1s" begin="3.0s" repeatCount="indefinite"/>
      </circle>
      {/* Second ring, slightly delayed for depth */}
      <circle cx="287" cy="122" r="3" fill="none" stroke="#4ade80" strokeWidth="0.8">
        <animate attributeName="r" values="3;30;3" dur="1s" begin="3.1s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.5;0;0.5" dur="1s" begin="3.1s" repeatCount="indefinite"/>
      </circle>
      {/* RX label */}
      <text x="287" y="135" fontSize="4" fill="rgba(74,222,128,0.55)" fontFamily="monospace" textAnchor="middle">RX</text>
    </svg>
  );
}

/** Linear actuator motor – the core product ReclinerDoctor sells */
function LinearActuator({ style }) {
  return (
    <svg viewBox="0 0 220 80" fill="none" style={style} className="absolute furniture-float-2">
      {/* Main tube body */}
      <rect x="10" y="25" width="150" height="30" rx="8" fill="#4a4a5a"/>
      <rect x="12" y="27" width="146" height="26" rx="7" fill="#5a5a6e"/>
      {/* Metallic sheen */}
      <rect x="12" y="27" width="146" height="8" rx="7" fill="rgba(255,255,255,0.12)"/>

      {/* Extendable rod */}
      <rect x="155" y="31" width="55" height="18" rx="5" fill="#7a7a8e"/>
      <rect x="157" y="33" width="51" height="14" rx="4" fill="#9a9aae"/>
      <rect x="157" y="33" width="51" height="5" rx="4" fill="rgba(255,255,255,0.15)"/>
      {/* Rod tip */}
      <circle cx="210" cy="40" r="7" fill="#6a6a7e"/>
      <circle cx="210" cy="40" r="4" fill="#8a8a9e"/>

      {/* Motor housing end */}
      <rect x="0" y="20" width="18" height="40" rx="6" fill="#3a3a4a"/>
      <circle cx="9" cy="40" r="5" fill="#2a2a3a"/>
      <circle cx="9" cy="40" r="2.5" fill="#1a1a2a"/>

      {/* Cable connector */}
      <rect x="60" y="10" width="16" height="16" rx="3" fill="#3a3a4a"/>
      <line x1="68" y1="26" x2="68" y2="25" stroke="#2a2a3a" strokeWidth="2"/>
      <rect x="63" y="5" width="10" height="8" rx="2" fill="#2a2a3a"/>
      {/* Wires */}
      <path d="M65 5 Q62 0 58 0" stroke="#e63946" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M71 5 Q74 0 78 0" stroke="#1d3557" strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* Brand stripe */}
      <rect x="30" y="36" width="90" height="8" rx="2" fill="rgba(255,255,255,0.06)"/>
      <rect x="32" y="37" width="20" height="6" rx="1" fill="rgba(255,200,50,0.25)"/>

      {/* Bolt details */}
      <circle cx="25" cy="40" r="4" fill="#3a3a4a"/>
      <circle cx="25" cy="40" r="2" fill="#2a2a3a"/>
      <circle cx="140" cy="40" r="4" fill="#3a3a4a"/>
      <circle cx="140" cy="40" r="2" fill="#2a2a3a"/>

      {/* Label */}
      <text x="32" y="38.5" fontSize="4" fill="rgba(255,220,100,0.5)" fontFamily="monospace">LINEAR ACTUATOR</text>
    </svg>
  );
}

/** Hand controller / remote – 5-button recliner remote with blinking LEDs */
function HandController({ style }) {
  return (
    <svg viewBox="0 0 80 160" fill="none" style={style} className="absolute furniture-float-3">
      {/* Body */}
      <rect x="8" y="10" width="64" height="130" rx="18" fill="#2c2c3e"/>
      <rect x="10" y="12" width="60" height="126" rx="16" fill="#38384e"/>
      {/* Top sheen */}
      <rect x="10" y="12" width="60" height="20" rx="16" fill="rgba(255,255,255,0.08)"/>

      {/* Cable at top */}
      <rect x="32" y="2" width="16" height="14" rx="4" fill="#222230"/>
      <path d="M40 2 Q40 -6 40 -10" stroke="#555568" strokeWidth="3" strokeLinecap="round"/>

      {/* UP button */}
      <rect x="24" y="30" width="32" height="20" rx="6" fill="#4a4a60"/>
      <rect x="25" y="31" width="30" height="8" rx="5" fill="rgba(255,255,255,0.1)"/>
      <polygon points="40,34 34,44 46,44" fill="#f59e0b" opacity="0.9"/>

      {/* RECLINE button */}
      <rect x="18" y="58" width="44" height="20" rx="6" fill="#3a5a8a"/>
      <rect x="19" y="59" width="42" height="8" rx="5" fill="rgba(255,255,255,0.1)"/>
      <text x="40" y="72" fontSize="5.5" fill="#93c5fd" textAnchor="middle" fontFamily="sans-serif" fontWeight="bold">RECLINE</text>

      {/* FLAT button */}
      <rect x="18" y="86" width="44" height="20" rx="6" fill="#3a5a8a"/>
      <rect x="19" y="87" width="42" height="8" rx="5" fill="rgba(255,255,255,0.1)"/>
      <text x="40" y="100" fontSize="5.5" fill="#93c5fd" textAnchor="middle" fontFamily="sans-serif" fontWeight="bold">FLAT</text>

      {/* DOWN button */}
      <rect x="24" y="114" width="32" height="20" rx="6" fill="#4a4a60"/>
      <rect x="25" y="115" width="30" height="8" rx="5" fill="rgba(255,255,255,0.1)"/>
      <polygon points="40,130 34,120 46,120" fill="#f59e0b" opacity="0.9"/>

      {/* ── Green LED – steady power-on heartbeat ── */}
      {/* Outer glow */}
      <circle cx="50" cy="22" r="6" fill="#22c55e">
        <animate attributeName="opacity" values="0.18;0.04;0.18;0.18;0.04;0.18"
          keyTimes="0;0.12;0.25;0.55;0.67;0.8" dur="2.2s" repeatCount="indefinite"/>
        <animate attributeName="r" values="6;4;6;6;4;6"
          keyTimes="0;0.12;0.25;0.55;0.67;0.8" dur="2.2s" repeatCount="indefinite"/>
      </circle>
      {/* Core dot */}
      <circle cx="50" cy="22" r="3" fill="#22c55e">
        <animate attributeName="opacity" values="1;0.2;1;1;0.2;1"
          keyTimes="0;0.12;0.25;0.55;0.67;0.8" dur="2.2s" repeatCount="indefinite"/>
      </circle>

      {/* ── Red LED – activity blink (double-flash pattern like a real controller) ── */}
      {/* Outer glow */}
      <circle cx="62" cy="22" r="6" fill="#ef4444">
        <animate attributeName="opacity"
          values="0.03;0.32;0.03;0.32;0.03;0.03;0.03"
          keyTimes="0;0.07;0.14;0.21;0.28;0.45;1"
          dur="3.8s" repeatCount="indefinite"/>
        <animate attributeName="r" values="4;7;4;7;4;4;4"
          keyTimes="0;0.07;0.14;0.21;0.28;0.45;1"
          dur="3.8s" repeatCount="indefinite"/>
      </circle>
      {/* Core dot */}
      <circle cx="62" cy="22" r="3" fill="#ef4444">
        <animate attributeName="opacity"
          values="0.1;1;0.1;1;0.1;0.1;0.1"
          keyTimes="0;0.07;0.14;0.21;0.28;0.45;1"
          dur="3.8s" repeatCount="indefinite"/>
      </circle>

      {/* Grip texture lines */}
      <line x1="20" y1="145" x2="60" y2="145" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="20" y1="150" x2="60" y2="150" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" strokeLinecap="round"/>

      {/* ── RF signal transmission – arcs expanding left from controller body ── */}
      <defs>
        <clipPath id="txWaveClip">
          {/* Only show left of the body edge (x < 8) */}
          <rect x="-80" y="0" width="88" height="160"/>
        </clipPath>
      </defs>
      {/* Three staggered expanding arcs */}
      {[0, 0.73, 1.46].map((delay, i) => (
        <circle key={i} cx="8" cy="76" r="5" fill="none"
          stroke="#f59e0b" strokeWidth={2.4 - i * 0.55}
          clipPath="url(#txWaveClip)">
          <animate attributeName="r" values="5;42" dur="2.2s" begin={`${delay}s`} repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.88;0" dur="2.2s" begin={`${delay}s`} repeatCount="indefinite"/>
        </circle>
      ))}
      {/* TX label below waves */}
      <text x="4" y="157" fontSize="4.5" fill="rgba(245,158,11,0.5)" fontFamily="monospace">TX</text>
    </svg>
  );
}

/** Power supply / transformer brick – with power-on LED pulse and energy flow */
function PowerSupply({ style }) {
  const cablePath = "M130 42 Q145 42 150 38 Q155 34 155 28";
  return (
    <svg viewBox="0 0 160 90" fill="none" style={style} className="absolute furniture-float-4">
      {/* Main brick */}
      <rect x="5" y="10" width="110" height="65" rx="10" fill="#2a2a3a"/>
      <rect x="7" y="12" width="106" height="61" rx="9" fill="#343448"/>
      {/* Top highlight */}
      <rect x="7" y="12" width="106" height="16" rx="9" fill="rgba(255,255,255,0.07)"/>

      {/* Label area */}
      <rect x="18" y="24" width="80" height="36" rx="5" fill="#222230"/>
      <text x="58" y="36" fontSize="5" fill="rgba(255,220,100,0.6)" textAnchor="middle" fontFamily="monospace">RECLINERDOCTOR</text>
      <text x="58" y="44" fontSize="4" fill="rgba(255,255,255,0.35)" textAnchor="middle" fontFamily="monospace">29V DC  2.0A</text>
      <text x="58" y="52" fontSize="4" fill="rgba(255,255,255,0.25)" textAnchor="middle" fontFamily="monospace">INPUT: 100-240V~</text>

      {/* AC prongs (left side) with subtle electricity shimmer */}
      <rect x="0" y="28" width="8" height="6" rx="2" fill="#1a1a2a"/>
      <rect x="0" y="42" width="8" height="6" rx="2" fill="#1a1a2a"/>
      {/* Prong shimmer overlay */}
      <rect x="0" y="28" width="8" height="6" rx="2" fill="#7090ff">
        <animate attributeName="opacity" values="0;0.35;0;0.35;0;0" keyTimes="0;0.04;0.09;0.13;0.18;1" dur="2.5s" repeatCount="indefinite"/>
      </rect>
      <rect x="0" y="42" width="8" height="6" rx="2" fill="#7090ff">
        <animate attributeName="opacity" values="0;0.35;0;0.35;0;0" keyTimes="0;0.04;0.09;0.13;0.18;1" dur="2.5s" repeatCount="indefinite"/>
      </rect>

      {/* DC barrel connector (right) */}
      <rect x="112" y="32" width="18" height="20" rx="4" fill="#1e1e2e"/>
      <circle cx="121" cy="42" r="6" fill="#2a2a3a"/>
      <circle cx="121" cy="42" r="3" fill="#0a0a1a"/>
      {/* DC connector active glow */}
      <circle cx="121" cy="42" r="9" fill="#4ade80">
        <animate attributeName="opacity" values="0.04;0.14;0.04" dur="1.6s" repeatCount="indefinite"/>
      </circle>

      {/* Output cable */}
      <path d={cablePath} stroke="#4a4a5a" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <circle cx="155" cy="26" r="5" fill="#3a3a4a"/>
      <circle cx="155" cy="26" r="2.5" fill="#1a1a2a"/>

      {/* ── Power flow dots travelling along the cable (3 staggered) ── */}
      <circle r="2.2" fill="#4ade80">
        <animateMotion dur="1.4s" repeatCount="indefinite" begin="0s" path={cablePath}/>
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.88;1" dur="1.4s" repeatCount="indefinite" begin="0s"/>
      </circle>
      <circle r="2.2" fill="#4ade80">
        <animateMotion dur="1.4s" repeatCount="indefinite" begin="0.47s" path={cablePath}/>
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.88;1" dur="1.4s" repeatCount="indefinite" begin="0.47s"/>
      </circle>
      <circle r="2.2" fill="#4ade80">
        <animateMotion dur="1.4s" repeatCount="indefinite" begin="0.93s" path={cablePath}/>
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.88;1" dur="1.4s" repeatCount="indefinite" begin="0.93s"/>
      </circle>

      {/* ── Status LED – breathing green pulse (power on) ── */}
      {/* Outer glow ring */}
      <circle cx="100" cy="20" fill="#4ade80">
        <animate attributeName="r" values="5;9;5" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.22;0.06;0.22" dur="1.6s" repeatCount="indefinite"/>
      </circle>
      {/* Core */}
      <circle cx="100" cy="20" r="3" fill="#4ade80">
        <animate attributeName="opacity" values="1;0.55;1" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="r" values="3;3.6;3" dur="1.6s" repeatCount="indefinite"/>
      </circle>
    </svg>
  );
}

/** Recliner mechanism frame – the steel scissor base */
function ReclineMechanism({ style }) {
  return (
    <svg viewBox="0 0 260 130" fill="none" style={style} className="absolute furniture-float-2">
      {/* Base rails */}
      <rect x="10" y="100" width="240" height="8" rx="3" fill="#8a7a50"/>
      <rect x="10" y="108" width="240" height="3" rx="1.5" fill="#6a5a30"/>

      {/* Front seat rail */}
      <rect x="10" y="60" width="240" height="8" rx="3" fill="#8a7a50"/>

      {/* Left scissor arm */}
      <line x1="40" y1="105" x2="90" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <line x1="90" y1="105" x2="40" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <circle cx="65" cy="83" r="7" fill="#e0b840"/>
      <circle cx="65" cy="83" r="3.5" fill="#c8a030"/>

      {/* Center scissor arm */}
      <line x1="115" y1="105" x2="145" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <line x1="145" y1="105" x2="115" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <circle cx="130" cy="83" r="7" fill="#e0b840"/>
      <circle cx="130" cy="83" r="3.5" fill="#c8a030"/>

      {/* Right scissor arm */}
      <line x1="170" y1="105" x2="220" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <line x1="220" y1="105" x2="170" y2="62" stroke="#c8a030" strokeWidth="6" strokeLinecap="round"/>
      <circle cx="195" cy="83" r="7" fill="#e0b840"/>
      <circle cx="195" cy="83" r="3.5" fill="#c8a030"/>

      {/* Actuator rod across center */}
      <rect x="50" y="80" width="160" height="8" rx="3" fill="#5a5a6e"/>
      <rect x="52" y="81" width="156" height="4" rx="2" fill="#7a7a8e"/>
      <rect x="50" y="82" width="16" height="5" rx="2" fill="#9a9aae"/>
      <rect x="194" y="82" width="16" height="5" rx="2" fill="#9a9aae"/>

      {/* Mounting brackets */}
      {[18, 232].map((x, i) => (
        <g key={i}>
          <rect x={x-4} y="92" width="16" height="20" rx="3" fill="#6a6a50"/>
          <circle cx={x+4} cy="110" r="3" fill="#4a4a30"/>
        </g>
      ))}

      {/* Label */}
      <text x="130" y="50" fontSize="6" fill="rgba(255,200,80,0.4)" textAnchor="middle" fontFamily="monospace">WALL-HUGGER MECHANISM</text>
    </svg>
  );
}

/** Upright sofa (2-seater) */
function Sofa({ style }) {
  return (
    <svg viewBox="0 0 280 160" fill="none" style={style} className="absolute furniture-float-3">
      {/* Body */}
      <rect x="24" y="70" width="232" height="72" rx="16" fill="#5c4a7a"/>
      {/* Seat cushions */}
      <rect x="34" y="82" width="98" height="52" rx="12" fill="#7060a0"/>
      <rect x="148" y="82" width="98" height="52" rx="12" fill="#7060a0"/>
      {/* Cushion highlight */}
      <rect x="34" y="82" width="98" height="14" rx="12" fill="rgba(255,255,255,0.08)"/>
      <rect x="148" y="82" width="98" height="14" rx="12" fill="rgba(255,255,255,0.08)"/>
      {/* Back cushions */}
      <rect x="34" y="48" width="98" height="38" rx="10" fill="#6050908"/>
      <rect x="34" y="48" width="98" height="38" rx="10" fill="#685888"/>
      <rect x="148" y="48" width="98" height="38" rx="10" fill="#685888"/>
      {/* Back highlight */}
      <rect x="34" y="48" width="98" height="10" rx="10" fill="rgba(255,255,255,0.07)"/>
      <rect x="148" y="48" width="98" height="10" rx="10" fill="rgba(255,255,255,0.07)"/>
      {/* Arms */}
      <rect x="4" y="58" width="28" height="72" rx="12" fill="#4a3868"/>
      <rect x="248" y="58" width="28" height="72" rx="12" fill="#4a3868"/>
      {/* Tufting */}
      {[70,91,112].map(x => <circle key={x} cx={x} cy="108" r="3" fill="#4a3868" opacity="0.6"/>)}
      {[180,202,224].map(x => <circle key={x} cx={x} cy="108" r="3" fill="#4a3868" opacity="0.6"/>)}
      {/* Legs */}
      {[38,110,166,238].map(x => <rect key={x} x={x} y="138" width="10" height="18" rx="3" fill="#2e2040"/>)}
      {/* Piping trim */}
      <line x1="24" y1="86" x2="256" y2="86" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5"/>
    </svg>
  );
}

/** Upright recliner – sitting position, traditional barrel shape */
function UprightRecliner({ style }) {
  return (
    <svg viewBox="0 0 200 220" fill="none" style={style} className="absolute furniture-float-4">
      {/* Base */}
      <rect x="30" y="185" width="140" height="14" rx="5" fill="#3d2010"/>
      {[40,80,110,152].map(x => <rect key={x} x={x} y="196" width="10" height="18" rx="3" fill="#2a1508"/>)}

      {/* Seat */}
      <rect x="22" y="130" width="156" height="58" rx="14" fill="#7a4a28"/>
      <rect x="30" y="136" width="140" height="46" rx="10" fill="#9a6040"/>
      <rect x="30" y="136" width="140" height="14" rx="10" fill="rgba(255,255,255,0.08)"/>
      {/* Seat tufting */}
      {[70,100,130].map(x => <circle key={x} cx={x} cy="162" r="3.5" fill="#6a3820" opacity="0.7"/>)}

      {/* Back – upright */}
      <rect x="28" y="42" width="144" height="92" rx="14" fill="#7a4a28"/>
      <rect x="36" y="50" width="128" height="78" rx="10" fill="#9a6040"/>
      <rect x="36" y="50" width="128" height="18" rx="10" fill="rgba(255,255,255,0.08)"/>
      {/* Back tufting */}
      {[70,100,130].map(x => [72,100].map(y => <circle key={`${x}${y}`} cx={x} cy={y} r="3" fill="#6a3820" opacity="0.6"/>))}

      {/* Headrest */}
      <rect x="42" y="20" width="116" height="30" rx="12" fill="#8a5535"/>
      <rect x="48" y="24" width="104" height="20" rx="9" fill="#aa7050"/>

      {/* Arms */}
      <rect x="4" y="118" width="26" height="70" rx="11" fill="#6a3820"/>
      <rect x="8" y="118" width="18" height="18" rx="8" fill="#8a5535"/>
      <rect x="170" y="118" width="26" height="70" rx="11" fill="#6a3820"/>
      <rect x="174" y="118" width="18" height="18" rx="8" fill="#8a5535"/>

      {/* Mechanism hint at base */}
      <rect x="55" y="178" width="90" height="8" rx="3" fill="#c8a030" opacity="0.35"/>
    </svg>
  );
}

/** Electric lift chair – slightly elevated, with footrest out */
function LiftChair({ style }) {
  return (
    <svg viewBox="0 0 220 240" fill="none" style={style} className="absolute furniture-float-1">
      {/* Lift base platform */}
      <rect x="20" y="195" width="180" height="20" rx="7" fill="#1e3a5a"/>
      <rect x="20" y="208" width="180" height="7" rx="3.5" fill="#162a42"/>
      {/* Lift column (raised) */}
      <rect x="72" y="155" width="76" height="44" rx="6" fill="#243f5e"/>
      <rect x="78" y="158" width="64" height="38" rx="4" fill="#2e5070"/>
      {/* Actuator rod on lift column */}
      <rect x="84" y="172" width="52" height="6" rx="2" fill="#5a8ab0" opacity="0.6"/>

      {/* Seat */}
      <rect x="18" y="118" width="184" height="52" rx="14" fill="#2a6080"/>
      <rect x="26" y="124" width="168" height="40" rx="10" fill="#3a78a0"/>
      <rect x="26" y="124" width="168" height="12" rx="10" fill="rgba(255,255,255,0.1)"/>
      {[80,110,140].map(x => <circle key={x} cx={x} cy="148" r="3" fill="#1e4860" opacity="0.7"/>)}

      {/* Back */}
      <rect x="24" y="34" width="172" height="88" rx="14" fill="#2a6080"/>
      <rect x="32" y="42" width="156" height="74" rx="10" fill="#3a78a0"/>
      <rect x="32" y="42" width="156" height="16" rx="10" fill="rgba(255,255,255,0.1)"/>
      {[80,110,140].map(x => [60,88].map(y => <circle key={`${x}${y}`} cx={x} cy={y} r="2.8" fill="#1e4860" opacity="0.6"/>))}

      {/* Headrest */}
      <rect x="44" y="10" width="132" height="30" rx="11" fill="#326a90"/>
      <rect x="50" y="14" width="120" height="20" rx="8" fill="#4290b8"/>

      {/* Arms */}
      <rect x="2" y="106" width="24" height="66" rx="10" fill="#22506e"/>
      <rect x="6" y="106" width="16" height="16" rx="7" fill="#326a90"/>
      <rect x="194" y="106" width="24" height="66" rx="10" fill="#22506e"/>
      <rect x="198" y="106" width="16" height="16" rx="7" fill="#326a90"/>

      {/* Footrest extended */}
      <g transform="rotate(-15 30 165)">
        <rect x="-10" y="132" width="80" height="36" rx="10" fill="#2a6080"/>
        <rect x="-4" y="137" width="68" height="24" rx="7" fill="#3a78a0"/>
        {[14,38].map(x => <circle key={x} cx={x} cy="149" r="2.5" fill="#1e4860" opacity="0.6"/>)}
      </g>

      {/* Hand controller dangling */}
      <path d="M196 130 Q210 135 210 148" stroke="#1e3a5a" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <rect x="204" y="146" width="14" height="22" rx="5" fill="#1e3a5a"/>
      <rect x="206" y="150" width="10" height="5" rx="2" fill="#2a5a80"/>
      <rect x="206" y="158" width="10" height="5" rx="2" fill="#2a5a80"/>

      {/* LED on arm */}
      <circle cx="10" cy="114" r="2.5" fill="#4ade80" opacity="0.85"/>
    </svg>
  );
}

/** 3-seater sofa – wide, with rolled arms */
function ThreeSeaterSofa({ style }) {
  return (
    <svg viewBox="0 0 380 180" fill="none" style={style} className="absolute furniture-float-2">
      {/* Body frame */}
      <rect x="28" y="78" width="324" height="80" rx="18" fill="#4a3525"/>
      {/* Three seat cushions */}
      {[38, 146, 254].map(x => (
        <g key={x}>
          <rect x={x} y="90" width="92" height="58" rx="12" fill="#6b4c32"/>
          <rect x={x+4} y="94" width="84" height="50" rx="9" fill="#7d5a3c"/>
          <rect x={x+4} y="94" width="84" height="14" rx="9" fill="rgba(255,255,255,0.08)"/>
          {/* Tufting */}
          {[x+24, x+46, x+68].map(tx => <circle key={tx} cx={tx} cy="120" r="3" fill="#4a3525" opacity="0.6"/>)}
        </g>
      ))}
      {/* Three back cushions */}
      {[38, 146, 254].map(x => (
        <g key={`b${x}`}>
          <rect x={x} y="48" width="92" height="46" rx="10" fill="#5e422c"/>
          <rect x={x+4} y="52" width="84" height="38" rx="8" fill="#7d5a3c"/>
          <rect x={x+4} y="52" width="84" height="12" rx="8" fill="rgba(255,255,255,0.07)"/>
        </g>
      ))}
      {/* Rolled arms */}
      <ellipse cx="24" cy="118" rx="18" ry="38" fill="#3e2d1e"/>
      <ellipse cx="22" cy="108" rx="13" ry="24" fill="#5e422c"/>
      <ellipse cx="356" cy="118" rx="18" ry="38" fill="#3e2d1e"/>
      <ellipse cx="358" cy="108" rx="13" ry="24" fill="#5e422c"/>
      {/* Legs */}
      {[48, 140, 230, 322].map(x => <rect key={x} x={x} y="154" width="12" height="20" rx="4" fill="#2a1e12"/>)}
      {/* Piping */}
      <line x1="28" y1="94" x2="352" y2="94" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5"/>
      <line x1="28" y1="150" x2="352" y2="150" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
    </svg>
  );
}

/** L-shaped sectional sofa */
function Sectional({ style }) {
  return (
    <svg viewBox="0 0 340 280" fill="none" style={style} className="absolute furniture-float-3">
      {/* Horizontal section */}
      <rect x="0" y="140" width="260" height="80" rx="16" fill="#3a4a30"/>
      {[10,100,190].map(x => (
        <g key={x}>
          <rect x={x+4} y="150" width="80" height="62" rx="10" fill="#506840"/>
          <rect x={x+6} y="152" width="76" height="58" rx="8" fill="#5e7a4a"/>
          <rect x={x+6} y="152" width="76" height="14" rx="8" fill="rgba(255,255,255,0.07)"/>
          {[x+24,x+46,x+64].map(tx => <circle key={tx} cx={tx} cy="180" r="2.8" fill="#3a4a30" opacity="0.6"/>)}
        </g>
      ))}
      {/* Horizontal back */}
      {[10,100,190].map(x => (
        <g key={`hb${x}`}>
          <rect x={x+4} y="112" width="80" height="32" rx="8" fill="#465c38"/>
          <rect x={x+6} y="114" width="76" height="26" rx="6" fill="#5e7a4a"/>
          <rect x={x+6} y="114" width="76" height="9" rx="6" fill="rgba(255,255,255,0.06)"/>
        </g>
      ))}
      {/* Horizontal legs */}
      {[14,90,170,242].map(x => <rect key={x} x={x} y="216" width="10" height="16" rx="3" fill="#28331e"/>)}

      {/* Vertical section (chaise) */}
      <rect x="218" y="0" width="80" height="160" rx="16" fill="#3a4a30"/>
      {[10,80].map(y => (
        <g key={y}>
          <rect x="228" y={y+4} width="62" height="68" rx="10" fill="#506840"/>
          <rect x="230" y={y+6} width="58" height="64" rx="8" fill="#5e7a4a"/>
          <rect x="230" y={y+6} width="58" height="16" rx="8" fill="rgba(255,255,255,0.07)"/>
          {[246,258,270].map(tx => <circle key={tx} cx={tx} cy={y+38} r="2.5" fill="#3a4a30" opacity="0.6"/>)}
        </g>
      ))}
      {/* Vertical back */}
      {[10,80].map(y => (
        <g key={`vb${y}`}>
          <rect x="194" y={y+4} width="28" height="68" rx="8" fill="#465c38"/>
          <rect x="196" y={y+6} width="24" height="64" rx="6" fill="#5e7a4a"/>
        </g>
      ))}
      {/* Vertical section legs */}
      {[230,278].map(x => <rect key={x} x={x} y="148" width="10" height="14" rx="3" fill="#28331e"/>)}

      {/* Corner connector block */}
      <rect x="218" y="140" width="82" height="80" rx="10" fill="#465c38"/>
      <rect x="222" y="144" width="74" height="72" rx="8" fill="#5e7a4a"/>
      <rect x="222" y="144" width="74" height="18" rx="8" fill="rgba(255,255,255,0.06)"/>

      {/* Left arm */}
      <rect x="0" y="136" width="18" height="80" rx="10" fill="#2e3c24"/>
      <rect x="2" y="136" width="14" height="18" rx="7" fill="#3a4a30"/>
    </svg>
  );
}

/* ── Pre-computed floating particle data ─────────────────────────────── */
const PARTICLES = [
  { l:  4, dur: 13, del:  0.0, sz: 1.5, c: '#f59e0b' },
  { l:  9, dur: 10, del:  2.2, sz: 1.0, c: '#60a5fa' },
  { l: 16, dur: 15, del:  5.1, sz: 2.0, c: '#f59e0b' },
  { l: 23, dur: 12, del:  1.0, sz: 1.5, c: '#4ade80' },
  { l: 29, dur:  9, del:  7.0, sz: 1.0, c: '#f59e0b' },
  { l: 36, dur: 14, del:  3.3, sz: 2.0, c: '#60a5fa' },
  { l: 43, dur: 11, del:  0.5, sz: 1.5, c: '#4ade80' },
  { l: 49, dur: 16, del:  4.0, sz: 1.0, c: '#f59e0b' },
  { l: 56, dur: 10, del:  8.0, sz: 2.0, c: '#60a5fa' },
  { l: 63, dur: 13, del:  1.5, sz: 1.5, c: '#f59e0b' },
  { l: 70, dur: 15, del:  6.0, sz: 1.0, c: '#4ade80' },
  { l: 77, dur:  9, del:  2.5, sz: 2.0, c: '#f59e0b' },
  { l: 84, dur: 14, del:  9.0, sz: 1.5, c: '#60a5fa' },
  { l: 91, dur: 12, del:  3.5, sz: 1.0, c: '#f59e0b' },
  { l: 96, dur: 11, del:  0.0, sz: 1.5, c: '#4ade80' },
  { l:  7, dur: 18, del:  6.0, sz: 1.0, c: '#60a5fa' },
  { l: 20, dur: 20, del: 11.0, sz: 1.0, c: '#f59e0b' },
  { l: 33, dur: 17, del:  8.5, sz: 1.5, c: '#4ade80' },
  { l: 52, dur: 19, del: 10.0, sz: 1.0, c: '#f59e0b' },
  { l: 66, dur: 16, del:  7.5, sz: 1.0, c: '#60a5fa' },
  { l: 79, dur: 18, del:  4.0, sz: 2.0, c: '#f59e0b' },
  { l: 88, dur: 20, del: 12.0, sz: 1.0, c: '#4ade80' },
];

/* ── Animated PCB-style circuit traces ───────────────────────────────── */
function CircuitTraces() {
  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 680"
      preserveAspectRatio="xMidYMid slice" style={{ opacity: 0.9 }}>
      <defs>
        <filter id="traceGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Green – power zone → actuator */}
      <path d="M 858 622 L 858 435 L 788 435 L 788 98"
        stroke="rgba(74,222,128,0.3)" strokeWidth="1.5" fill="none"
        strokeDasharray="8 4" filter="url(#traceGlow)">
        <animate attributeName="stroke-dashoffset" from="12" to="0" dur="2.2s" repeatCount="indefinite"/>
      </path>
      {/* Amber – controller → recliner */}
      <path d="M 893 375 L 598 375 L 598 622 L 178 622"
        stroke="rgba(245,158,11,0.24)" strokeWidth="1.5" fill="none" strokeDasharray="8 4">
        <animate attributeName="stroke-dashoffset" from="0" to="12" dur="3s" repeatCount="indefinite"/>
      </path>
      {/* Blue – mechanism → actuator */}
      <path d="M 58 218 L 338 218 L 338 108 L 638 108"
        stroke="rgba(96,165,250,0.24)" strokeWidth="1.5" fill="none" strokeDasharray="6 4">
        <animate attributeName="stroke-dashoffset" from="10" to="0" dur="4s" repeatCount="indefinite"/>
      </path>
      {/* Purple – short accent connector */}
      <path d="M 498 342 L 648 342 L 648 278"
        stroke="rgba(192,132,252,0.22)" strokeWidth="1.2" fill="none" strokeDasharray="5 4">
        <animate attributeName="stroke-dashoffset" from="0" to="9" dur="2.5s" repeatCount="indefinite"/>
      </path>
      {/* Junction nodes */}
      {[
        { cx: 858, cy: 435, c: 'rgba(74,222,128,0.6)',  dur: '2.2s' },
        { cx: 598, cy: 375, c: 'rgba(245,158,11,0.6)',  dur: '3s'   },
        { cx: 338, cy: 218, c: 'rgba(96,165,250,0.6)',  dur: '4s'   },
        { cx: 648, cy: 342, c: 'rgba(192,132,252,0.55)',dur: '2.5s' },
      ].map(({ cx, cy, c, dur }) => (
        <circle key={`${cx}${cy}`} cx={cx} cy={cy} r="3.5" fill={c}>
          <animate attributeName="opacity" values="0.5;1;0.5" dur={dur} repeatCount="indefinite"/>
          <animate attributeName="r" values="3;5;3" dur={dur} repeatCount="indefinite"/>
        </circle>
      ))}
    </svg>
  );
}

/* ── Actuator telemetry HUD panel ────────────────────────────────────── */
function MotorHUD({ style }) {
  // Two-period sine wave path (period = 120 user units) for seamless scroll
  const wave = 'M0,15 C10,15 20,5 30,5 C40,5 50,15 60,15 C70,15 80,25 90,25 C100,25 110,15 120,15'
    + ' C130,15 140,5 150,5 C160,5 170,15 180,15 C190,15 200,25 210,25 C220,25 230,15 240,15';
  return (
    <div style={{ ...style, position: 'absolute', width: 158, borderRadius: 10, overflow: 'hidden',
      background: 'rgba(3,6,18,0.92)', border: '1px solid rgba(245,158,11,0.22)',
      boxShadow: '0 0 40px rgba(245,158,11,0.08), 0 0 1px rgba(245,158,11,0.5), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
      {/* Title bar */}
      <div style={{ padding: '6px 10px', borderBottom: '1px solid rgba(245,158,11,0.12)',
        background: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#f59e0b',
          boxShadow: '0 0 8px #f59e0b', animation: 'ledPulse 2s ease-in-out infinite' }}/>
        <span style={{ fontSize: 6.5, color: '#fcd34d', fontFamily: 'monospace',
          letterSpacing: '0.12em', fontWeight: 'bold' }}>ACTUATOR TELEMETRY</span>
      </div>
      {/* Oscilloscope */}
      <div style={{ padding: '6px 8px 4px' }}>
        <div style={{ fontSize: 5.5, color: 'rgba(74,222,128,0.45)', fontFamily: 'monospace', marginBottom: 3 }}>
          OUTPUT WAVEFORM
        </div>
        <div style={{ overflow: 'hidden', height: 34, borderRadius: 4,
          background: 'rgba(0,16,7,0.98)', border: '1px solid rgba(74,222,128,0.15)' }}>
          <svg viewBox="0 0 120 30" style={{ width: '200%', height: '100%', display: 'block' }}>
            <line x1="0" y1="15" x2="240" y2="15" stroke="rgba(74,222,128,0.12)" strokeWidth="0.5"/>
            {/* Animated wave – uses SVG animateTransform for correct unit space */}
            <g>
              <animateTransform attributeName="transform" type="translate"
                from="0 0" to="-120 0" dur="2s" repeatCount="indefinite" calcMode="linear"/>
              <path d={wave} stroke="#4ade80" strokeWidth="1.5" fill="none" opacity="0.92"/>
              <path d={wave} stroke="#4ade80" strokeWidth="6" fill="none" opacity="0.07"/>
            </g>
          </svg>
        </div>
      </div>
      {/* Readout grid */}
      <div style={{ padding: '2px 8px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {[
          { label: 'VOLTAGE', val: '29.0V', color: '#4ade80' },
          { label: 'CURRENT', val: '1.8A',  color: '#60a5fa' },
          { label: 'RPM',     val: '1250',  color: '#f59e0b' },
          { label: 'EXTEND',  val: '87%',   color: '#c084fc' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: 'rgba(0,0,0,0.5)', borderRadius: 5, padding: '4px 6px',
            border: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ fontSize: 5, color: 'rgba(255,255,255,0.28)', fontFamily: 'monospace', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 10, color, fontFamily: 'monospace', fontWeight: 'bold',
              textShadow: `0 0 12px ${color}` }}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Blueprint corner brackets ───────────────────────────────────────── */
function CornerBrackets() {
  const stroke = 'rgba(245,158,11,0.32)';
  const sw = 1.5;
  const sz = 32;
  return (
    <>
      <svg width={sz} height={sz} style={{ position:'absolute', top:14, left:14 }} viewBox={`0 0 ${sz} ${sz}`}>
        <path d={`M0 ${sz} L0 0 L${sz} 0`} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="square"/>
        <circle cx="0" cy="0" r="2" fill="rgba(245,158,11,0.5)"/>
      </svg>
      <svg width={sz} height={sz} style={{ position:'absolute', top:14, right:14 }} viewBox={`0 0 ${sz} ${sz}`}>
        <path d={`M0 0 L${sz} 0 L${sz} ${sz}`} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="square"/>
        <circle cx={sz} cy="0" r="2" fill="rgba(245,158,11,0.5)"/>
      </svg>
      <svg width={sz} height={sz} style={{ position:'absolute', bottom:14, left:14 }} viewBox={`0 0 ${sz} ${sz}`}>
        <path d={`M0 0 L0 ${sz} L${sz} ${sz}`} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="square"/>
        <circle cx="0" cy={sz} r="2" fill="rgba(245,158,11,0.5)"/>
      </svg>
      <svg width={sz} height={sz} style={{ position:'absolute', bottom:14, right:14 }} viewBox={`0 0 ${sz} ${sz}`}>
        <path d={`M${sz} 0 L${sz} ${sz} L0 ${sz}`} stroke={stroke} strokeWidth={sw} fill="none" strokeLinecap="square"/>
        <circle cx={sz} cy={sz} r="2" fill="rgba(245,158,11,0.5)"/>
      </svg>
    </>
  );
}

/* ── Background scene composer ────────────────────────────────────────── */
function FurnitureScene() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">

      {/* ── Deep space base ── */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(145deg, #040408 0%, #070b16 28%, #060a14 62%, #030608 100%)',
      }}/>

      {/* ── Nebula glow zones ── */}
      <div className="absolute" style={{ left: '7%', bottom: '6%', width: 640, height: 640,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(160,95,25,0.26) 0%, transparent 58%)' }}/>
      <div className="absolute" style={{ right: '5%', top: '3%', width: 520, height: 520,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(70,100,210,0.16) 0%, transparent 58%)' }}/>
      <div className="absolute" style={{ left: '42%', top: '18%', width: 420, height: 420,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(90,50,150,0.12) 0%, transparent 58%)' }}/>
      <div className="absolute" style={{ left: '50%', bottom: '-5%', transform: 'translateX(-50%)',
        width: 720, height: 360, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(130,70,30,0.2) 0%, transparent 58%)' }}/>

      {/* ── Blueprint grid (minor + major lines) ── */}
      <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.06 }}>
        <defs>
          <pattern id="gridMinor" width="42" height="42" patternUnits="userSpaceOnUse">
            <path d="M42 0 L0 0 0 42" fill="none" stroke="white" strokeWidth="0.4"/>
          </pattern>
          <pattern id="gridMajor" width="168" height="168" patternUnits="userSpaceOnUse">
            <rect width="168" height="168" fill="url(#gridMinor)"/>
            <path d="M168 0 L0 0 0 168" fill="none" stroke="white" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#gridMajor)"/>
      </svg>

      {/* ── Animated PCB circuit traces ── */}
      <CircuitTraces />

      {/* ── Scanning beam (slow horizontal sweep) ── */}
      <div style={{ position:'absolute', left:0, right:0, height:1, zIndex:2,
        background: 'linear-gradient(90deg, transparent 2%, rgba(245,158,11,0.55) 22%, rgba(140,210,255,0.4) 50%, rgba(245,158,11,0.55) 78%, transparent 98%)',
        boxShadow: '0 0 16px rgba(245,158,11,0.55), 0 0 50px rgba(140,210,255,0.22)',
        animation: 'scanBeam 11s linear infinite' }}/>
      {/* Soft glow trail below beam */}
      <div style={{ position:'absolute', left:0, right:0, height:70, zIndex:2,
        background: 'linear-gradient(180deg, rgba(245,158,11,0.07) 0%, transparent 100%)',
        animation: 'scanBeam 11s linear infinite' }}/>

      {/* ── Floating glowing particles ── */}
      {PARTICLES.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${p.l}%`, bottom: '-4px',
          width: p.sz, height: p.sz, borderRadius: '50%',
          background: p.c, boxShadow: `0 0 ${p.sz * 4}px ${p.c}80`,
          animation: `particleRise ${p.dur}s linear ${p.del}s infinite`,
        }}/>
      ))}

      {/* ── Furniture pieces ── */}
      <ReclinedChair     style={{ bottom: '2%', left: '-1%',  width: 420, opacity: 0.65 }}/>
      <ReclineMechanism  style={{ top:  '6%', left:  '2%',   width: 280, opacity: 0.60 }}/>
      <LinearActuator    style={{ top: '10%', right: '2%',   width: 240, opacity: 0.65 }}/>
      <HandController    style={{ right: '3%', top: '36%',   width: 100, opacity: 0.63 }}/>
      <PowerSupply       style={{ bottom: '6%', right: '2%', width: 200, opacity: 0.63 }}/>
      {/* Power Supply label – positioned directly below the image */}
      <div className="absolute flex items-center gap-2" style={{ bottom: '4%', right: '2%', opacity: 0.82 }}>
        <div style={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80',
            boxShadow: '0 0 10px #4ade80, 0 0 20px #4ade8060',
            animation: 'ledPulse 3s ease-in-out infinite' }}/>
          <div style={{ position:'absolute', top:'50%', left:'50%', width: 8, height: 8,
            borderRadius:'50%', border: '1.5px solid #4ade8090',
            transform:'translate(-50%,-50%)',
            animation: 'sonarPing 3s ease-out infinite' }}/>
          <div style={{ position:'absolute', top:'50%', left:'50%', width: 8, height: 8,
            borderRadius:'50%', border: '1px solid #4ade8050',
            transform:'translate(-50%,-50%)',
            animation: 'sonarPing 3s ease-out 1.5s infinite' }}/>
        </div>
        <span style={{ fontSize: 9.5, fontFamily: 'monospace', color: '#fcd34d',
          letterSpacing: '0.1em', textTransform: 'uppercase',
          textShadow: '0 0 12px rgba(245,158,11,0.75)' }}>Power Supply</span>
      </div>
      <Sofa              style={{ top:  '3%', left: '53%',   width: 260, opacity: 0.50 }}/>
      <UprightRecliner   style={{ bottom: '4%', right: '20%',width: 200, opacity: 0.58 }}/>
      <LiftChair         style={{ top: '28%', left:  '1%',   width: 220, opacity: 0.55 }}/>
      <ThreeSeaterSofa   style={{ bottom: '1%', left: '28%', width: 360, opacity: 0.50 }}/>
      <Sectional         style={{ top:  '2%', left: '26%',   width: 260, opacity: 0.48 }}/>

      {/* ── Motor telemetry HUD ── */}
      <MotorHUD style={{ right: '14%', top: '55%' }}/>

      {/* ── Blueprint corner brackets ── */}
      <CornerBrackets />

      {/* ── Part labels with sonar-ping dots ── */}
      {[
        { x: '8%',  y: '35%', label: 'Scissor Mechanism', color: '#f59e0b' },
        { x: '72%', y: '15%', label: 'Linear Actuator',   color: '#60a5fa' },
        { x: '82%', y: '42%', label: 'Hand Controller',   color: '#f59e0b' },
        { x: '22%', y: '72%', label: 'Power Recliner',    color: '#f59e0b' },
        { x: '70%', y: '76%', label: 'Lift Chair',        color: '#f59e0b' },
        { x: '35%', y: '85%', label: '3-Seater Sofa',     color: '#60a5fa' },
        { x: '30%', y: '8%',  label: 'Sectional',         color: '#f59e0b' },
      ].map(({ x, y, label, color }) => (
        <div key={label} className="absolute flex items-center gap-2"
          style={{ left: x, top: y, opacity: 0.82 }}>
          {/* Dot + sonar ring */}
          <div style={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%',
              background: color, boxShadow: `0 0 10px ${color}, 0 0 20px ${color}60`,
              animation: 'ledPulse 3s ease-in-out infinite' }}/>
            <div style={{ position:'absolute', top:'50%', left:'50%', width: 8, height: 8,
              borderRadius:'50%', border: `1.5px solid ${color}90`,
              transform:'translate(-50%,-50%)',
              animation: 'sonarPing 3s ease-out infinite' }}/>
            <div style={{ position:'absolute', top:'50%', left:'50%', width: 8, height: 8,
              borderRadius:'50%', border: `1px solid ${color}50`,
              transform:'translate(-50%,-50%)',
              animation: 'sonarPing 3s ease-out 1.5s infinite' }}/>
          </div>
          <span style={{ fontSize: 9.5, fontFamily: 'monospace', color: '#fcd34d',
            letterSpacing: '0.1em', textTransform: 'uppercase',
            textShadow: `0 0 12px rgba(245,158,11,0.75)` }}>{label}</span>
        </div>
      ))}

      {/* ── Vignette to frame the scene ── */}
      <div style={{ position:'absolute', inset:0,
        background: 'radial-gradient(ellipse at center, transparent 28%, rgba(0,0,0,0.65) 100%)',
        pointerEvents: 'none' }}/>

      {/* ── All keyframes ── */}
      <style>{`
        @keyframes floatA {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(-14px) rotate(0.8deg); }
        }
        @keyframes floatB {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(-10px) rotate(-0.5deg); }
        }
        @keyframes floatC {
          0%,100% { transform: translateY(0px); }
          40%      { transform: translateY(-18px) rotate(1deg); }
          80%      { transform: translateY(-6px) rotate(-0.5deg); }
        }
        @keyframes floatD {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          60%      { transform: translateY(-12px) rotate(-0.8deg); }
        }
        .furniture-float-1 { animation: floatA 10s ease-in-out infinite; }
        .furniture-float-2 { animation: floatB 13s ease-in-out infinite; }
        .furniture-float-3 { animation: floatC 15s ease-in-out infinite; }
        .furniture-float-4 { animation: floatD  9s ease-in-out infinite; }

        @keyframes scanBeam {
          0%   { top: -2%; }
          100% { top: 104%; }
        }
        @keyframes particleRise {
          0%   { transform: translateY(0px) scale(1);   opacity: 0; }
          8%   { opacity: 1; }
          88%  { opacity: 0.75; }
          100% { transform: translateY(-100vh) scale(0.6); opacity: 0; }
        }
        @keyframes ledPulse {
          0%,100% { opacity: 1;   }
          50%      { opacity: 0.3; }
        }
        @keyframes sonarPing {
          0%   { transform: translate(-50%,-50%) scale(1);   opacity: 0.8; }
          100% { transform: translate(-50%,-50%) scale(4.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* ── OmniDrive logo mark ─────────────────────────────────────────────── */
function LogoMark() {
  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 16,
      padding: '12px 28px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(245,158,11,0.15)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src="/omnidrive-logo-cropped.png"
        alt="OmniDrive"
        style={{ height: 52, width: 'auto', display: 'block' }}
      />
    </div>
  );
}

/* ── Login Page ─────────────────────────────────────────────────────── */
export default function LoginPage() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isAuth     = useSelector(selectIsAuth);
  const portalBase = useSelector(selectPortalBase);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({ resolver: yupResolver(schema) });

  // Every platform administrator uses the shared full dashboard.
  const landingPath = (base) => `${base}/dashboard`;

  // Navigate as soon as auth state is valid — works reliably in React 18
  useEffect(() => {
    if (isAuth && portalBase) {
      navigate(landingPath(portalBase), { replace: true });
    }
  }, [isAuth, portalBase, navigate]);

  // Fallback: render-time redirect for users who are already logged in on mount
  if (isAuth && portalBase) return <Navigate to={landingPath(portalBase)} replace />;

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const apiRes = await authApi.login(data);
      const res = apiRes.data;          // unwrap ApiResponse<AuthResponse> → AuthResponse
      dispatch(setCredentials(res));
      const displayName = res.fullName
        || `${res.firstName ?? ''} ${res.lastName ?? ''}`.trim()
        || 'User';
      toast.success(`Welcome back, ${displayName}!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      <FurnitureScene />

      {/* ── Home button — top-left, always visible over the dark background ── */}
      <a
        href="/"
        style={{
          position:       'fixed',
          top:            18,
          left:           20,
          zIndex:         20,
          display:        'inline-flex',
          alignItems:     'center',
          gap:            6,
          padding:        '0.42rem 1rem',
          borderRadius:   '0.55rem',
          background:     'rgba(255,255,255,0.08)',
          border:         '1px solid rgba(255,255,255,0.18)',
          backdropFilter: 'blur(10px)',
          color:          'rgba(255,255,255,0.85)',
          fontSize:       13,
          fontWeight:     500,
          textDecoration: 'none',
          transition:     'background 0.2s, border-color 0.2s, color 0.2s',
          fontFamily:     'inherit',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background    = 'rgba(245,158,11,0.18)';
          e.currentTarget.style.borderColor   = 'rgba(245,158,11,0.55)';
          e.currentTarget.style.color         = '#fcd34d';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background    = 'rgba(255,255,255,0.08)';
          e.currentTarget.style.borderColor   = 'rgba(255,255,255,0.18)';
          e.currentTarget.style.color         = 'rgba(255,255,255,0.85)';
        }}
      >
        {/* Left arrow */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Home
      </a>

      <div className="relative w-full max-w-md z-10">
        {/* Logo */}
        <div className="text-center mb-7">
          <div className="flex justify-center mb-4"><LogoMark /></div>
          {/* <p className="text-sm mt-1" style={{ color: 'rgba(253,186,116,0.75)' }}>
            Recliner &amp; Furniture Parts Portal
          </p> */}
        </div>

        {/* Card */}
        <div className="rounded-3xl p-8"
          style={{
            background: 'rgba(255,255,255,0.97)',
            boxShadow: '0 30px 70px rgba(0,0,0,0.5), 0 0 0 1px rgba(245,158,11,0.15)',
          }}>
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900">Sign in</h2>
            <p className="text-sm text-gray-400 mt-0.5">Access your vendor or client account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email address</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                    strokeLinecap="round" className="h-4 w-4">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </span>
                <input {...register('email')} type="email" placeholder="you@example.com"
                  className="block w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm outline-none transition-all"
                  style={{ borderColor: errors.email ? '#ef4444' : '#e5e7eb' }}
                  onFocus={e => { e.target.style.boxShadow='0 0 0 3px rgba(245,158,11,0.2)'; e.target.style.borderColor='#f59e0b'; }}
                  onBlur={e  => { e.target.style.boxShadow=''; e.target.style.borderColor=errors.email?'#ef4444':'#e5e7eb'; }}
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                    strokeLinecap="round" className="h-4 w-4">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                </span>
                <input {...register('password')} type={showPw ? 'text' : 'password'} placeholder="••••••••"
                  className="block w-full pl-9 pr-10 py-2.5 border rounded-xl text-sm outline-none transition-all"
                  style={{ borderColor: errors.password ? '#ef4444' : '#e5e7eb' }}
                  onFocus={e => { e.target.style.boxShadow='0 0 0 3px rgba(245,158,11,0.2)'; e.target.style.borderColor='#f59e0b'; }}
                  onBlur={e  => { e.target.style.boxShadow=''; e.target.style.borderColor=errors.password?'#ef4444':'#e5e7eb'; }}
                />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-4 w-4">
                    {showPw
                      ? <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
                      : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                    }
                  </svg>
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-xs text-red-500">{errors.password.message}</p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg,#b45309,#d97706,#f59e0b)',
                boxShadow: '0 4px 18px rgba(217,119,6,0.45)',
              }}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                  Signing in…
                </span>
              ) : 'Sign in →'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-400">
            Don't have an account?{' '}
            <span className="font-medium" style={{ color: '#d97706' }}>
              Contact your OmniDrive administrator.
            </span>
          </p>
        </div>

        <p className="text-center text-xs mt-5" style={{ color: 'rgba(255,255,255,0.2)' }}>
          © {new Date().getFullYear()} OmniDrive · Powered by ReclinerDoctor
        </p>
      </div>
    </div>
  );
}
